import React, { useState } from 'react';
import { apiCall } from '../config';

function UserProfile({ user, onClose, onUserUpdate }) {
  const [formData, setFormData] = useState({
    firstname: user.firstname || '',
    lastname: user.lastname || '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('profile');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Fehler zurücksetzen wenn der User tippt
    if (error) setError('');
    if (success) setSuccess('');
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await apiCall(`/users/${user.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstname: formData.firstname,
          lastname: formData.lastname
        })
      });

      if (response.ok) {
        const updatedUser = response;
        setSuccess('Profil erfolgreich aktualisiert!');
        if (onUserUpdate) {
          onUserUpdate(updatedUser);
        }
      } else {
        const errorData = response;
        setError(errorData.error || 'Fehler beim Aktualisieren des Profils');
      }
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Profils:', error);
      setError('Verbindungsfehler zum Server');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Validierung
    // Für Admin-Benutzer mit leerem Passwort, automatisch leeres currentPassword verwenden
    const effectiveCurrentPassword = formData.currentPassword || '';

    if (!formData.newPassword) {
      setError('Bitte geben Sie ein neues Passwort ein');
      setLoading(false);
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('Die Passwörter stimmen nicht überein');
      setLoading(false);
      return;
    }

    if (formData.newPassword.length < 4) {
      setError('Das Passwort muss mindestens 4 Zeichen lang sein');
      setLoading(false);
      return;
    }

    try {
      const response = await apiCall(`/users/${user.id}/password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          currentPassword: effectiveCurrentPassword,
          newPassword: formData.newPassword
        })
      });

      if (response.ok) {
        setSuccess('Passwort erfolgreich geändert!');
        // Passwort-Felder zurücksetzen
        setFormData(prev => ({
          ...prev,
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        }));
      } else {
        const errorData = response;
        setError(errorData.error || 'Fehler beim Ändern des Passworts');
      }
    } catch (error) {
      console.error('Fehler beim Ändern des Passworts:', error);
      setError('Verbindungsfehler zum Server');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="bi bi-person-circle me-2"></i>
              Mein Profil
            </h5>
            <button 
              type="button" 
              className="btn-close" 
              onClick={onClose}
            ></button>
          </div>
          
          <div className="modal-body">
            {/* Tab Navigation */}
            <ul className="nav nav-tabs mb-4">
              <li className="nav-item">
                <button 
                  className={`nav-link ${activeTab === 'profile' ? 'active' : ''}`}
                  onClick={() => setActiveTab('profile')}
                >
                  <i className="bi bi-person-fill me-2"></i>
                  Profildaten
                </button>
              </li>
              <li className="nav-item">
                <button 
                  className={`nav-link ${activeTab === 'password' ? 'active' : ''}`}
                  onClick={() => setActiveTab('password')}
                >
                  <i className="bi bi-key-fill me-2"></i>
                  Passwort ändern
                </button>
              </li>
            </ul>

            {/* Error/Success Messages */}
            {error && (
              <div className="alert alert-danger" role="alert">
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                {error}
              </div>
            )}
            
            {success && (
              <div className="alert alert-success" role="alert">
                <i className="bi bi-check-circle-fill me-2"></i>
                {success}
              </div>
            )}

            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <form onSubmit={handleProfileUpdate}>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label htmlFor="firstname" className="form-label">
                      <i className="bi bi-person me-2"></i>Vorname
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="firstname"
                      name="firstname"
                      value={formData.firstname}
                      onChange={handleInputChange}
                      placeholder="Ihr Vorname"
                    />
                  </div>
                  
                  <div className="col-md-6 mb-3">
                    <label htmlFor="lastname" className="form-label">
                      <i className="bi bi-person me-2"></i>Nachname
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="lastname"
                      name="lastname"
                      value={formData.lastname}
                      onChange={handleInputChange}
                      placeholder="Ihr Nachname"
                      required
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">
                    <i className="bi bi-shield-fill me-2"></i>Rolle
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={user.role === 'admin' ? 'Administrator' : 'Mitarbeiter'}
                    disabled
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">
                    <i className="bi bi-hash me-2"></i>Benutzer-ID
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={user.id}
                    disabled
                  />
                </div>

                <div className="d-flex justify-content-end">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Speichere...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check-lg me-2"></i>
                        Profil speichern
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}

            {/* Password Tab */}
            {activeTab === 'password' && (
              <form onSubmit={handlePasswordChange}>
                <div className="mb-3">
                  <label htmlFor="currentPassword" className="form-label">
                    <i className="bi bi-key me-2"></i>Aktuelles Passwort
                  </label>
                  <input
                    type="password"
                    className="form-control"
                    id="currentPassword"
                    name="currentPassword"
                    value={formData.currentPassword}
                    onChange={handleInputChange}
                    placeholder="Aktuelles Passwort (z.B. leer oder 'admin')"
                  />
                  <small className="text-muted">
                    Wenn Sie noch kein Passwort haben, versuchen Sie: leer lassen, "admin", "admin123" oder "mitarbeiter"
                  </small>
                </div>

                <div className="mb-3">
                  <label htmlFor="newPassword" className="form-label">
                    <i className="bi bi-key-fill me-2"></i>Neues Passwort
                  </label>
                  <input
                    type="password"
                    className="form-control"
                    id="newPassword"
                    name="newPassword"
                    value={formData.newPassword}
                    onChange={handleInputChange}
                    placeholder="Ihr neues Passwort"
                    required
                  />
                  <small className="text-muted">Mindestens 4 Zeichen</small>
                </div>

                <div className="mb-3">
                  <label htmlFor="confirmPassword" className="form-label">
                    <i className="bi bi-key-fill me-2"></i>Passwort bestätigen
                  </label>
                  <input
                    type="password"
                    className="form-control"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    placeholder="Neues Passwort wiederholen"
                    required
                  />
                </div>

                <div className="d-flex justify-content-end">
                  <button
                    type="submit"
                    className="btn btn-warning"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Ändere...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-shield-lock-fill me-2"></i>
                        Passwort ändern
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
          
          <div className="modal-footer">
            <button 
              type="button" 
              className="btn btn-secondary" 
              onClick={onClose}
            >
              <i className="bi bi-x-lg me-2"></i>
              Schließen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UserProfile;
